import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { resolveCustomerShopFallbackPath } from '../../../features/auth/customerAuthPaths.js';
import { resolveHierarchicalBackTarget } from '../../../navigation/hierarchicalBack.js';
import { Button } from '../../ui/Button.js';
import { Card } from '../../ui/Card.js';

type Props = {
  onRetry: () => void;
  feature: string;
  testId: string;
};

/**
 * Stateful route-level error fallback. Nav-reset lives here only:
 * while mounted, a pathname change calls `onRetry` so RemountBoundary remounts.
 *
 * Public i18n copy only — never receives or renders Error.message / stack /
 * componentStack (RemountBoundary does not pass the caught error into this UI).
 */
export function RouteErrorFallback({ onRetry, feature, testId }: Props): JSX.Element {
  const { t } = useTranslation('customer');
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { tenantCode: tenantCodeParam } = useParams<{ tenantCode: string }>();
  const mountPathRef = useRef(pathname);
  const alertRef = useRef<HTMLDivElement>(null);

  const tenantFromPath = pathname.split('/').filter(Boolean)[0];
  const tenantCode =
    (tenantCodeParam !== undefined && tenantCodeParam.trim().length > 0
      ? tenantCodeParam.trim()
      : undefined) ??
    (tenantFromPath !== undefined && tenantFromPath.length > 0 ? tenantFromPath : undefined);

  useEffect(() => {
    if (pathname === mountPathRef.current) {
      return;
    }
    onRetry();
  }, [pathname, onRetry]);

  useEffect(() => {
    alertRef.current?.focus();
  }, []);

  const handleBack = (): void => {
    if (tenantCode === undefined) {
      navigate('/');
      return;
    }
    const hierarchical = resolveHierarchicalBackTarget(pathname, tenantCode, {
      search,
    });
    navigate(hierarchical?.parentPath ?? resolveCustomerShopFallbackPath(tenantCode));
  };

  return (
    <div
      ref={alertRef}
      role="alert"
      aria-live="assertive"
      tabIndex={-1}
      data-testid={testId}
      data-feature={feature}
      // w-full min-w-0 flex-col: under shell `flex-col`, bare `mx-auto` cancels stretch
      // and Card `container-type: inline-size` collapses width to ~padding (thin column).
      className="mx-auto flex min-h-[50vh] w-full min-w-0 max-w-lg flex-col justify-center p-6 outline-none"
    >
      <Card padded className="w-full min-w-0">
        <h2 className="text-lg font-semibold text-[var(--color-on-surface)]">
          {t('app.routeError.title')}
        </h2>
        <p className="mt-2 text-sm text-[var(--color-on-surface-muted)]">
          {t('app.routeError.message')}
        </p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Button type="button" intent="primary" size="md" onClick={onRetry}>
            {t('app.routeError.retry')}
          </Button>
          <Button
            type="button"
            intent="secondary"
            size="md"
            onClick={handleBack}
            data-testid="customer-route-error-back"
          >
            {t('app.routeError.back')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
